import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, TextField, Button, Typography, Alert, CircularProgress, Link } from '@mui/material';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import { supabase } from '../../supabaseClient';
import { serifVoice } from '../../theme';
import { useAuth } from '../../auth/AuthContext';

type Mode = 'password' | 'link' | 'reset';

/**
 * Split-panel layout follows ardent-scholar's login: brand left, form right,
 * brand collapsing to a wordmark under md. The content is Roamoir's own —
 * scholar sells a research programme, this sells a journal.
 *
 * Password is the default mode because this gets used on a phone, where a magic
 * link means leaving the browser for the mail app and coming back. The email
 * link stays available; it is still the only way to create an account.
 */
const PILLARS = [
  {
    icon: <MapOutlinedIcon sx={{ fontSize: 18 }} />,
    label: 'Plan by place and day',
    line: 'See the whole trip at once. Commit a place to a day when you’ve decided, not before.',
  },
  {
    icon: <CloudOffOutlinedIcon sx={{ fontSize: 18 }} />,
    label: 'Works with no signal',
    line: 'Write it in the gallery basement. It syncs when you surface.',
  },
  {
    icon: <PhotoCameraOutlinedIcon sx={{ fontSize: 18 }} />,
    label: 'Capture as you go',
    line: 'Photos and notes attach to the place you were standing in.',
  },
  {
    icon: <AutoStoriesOutlinedIcon sx={{ fontSize: 18 }} />,
    label: 'Publish a journal',
    line: 'Turn the trip into something you can send. No sign-in for whoever reads it.',
  },
];

export default function LoginPage() {
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<Mode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { session } = useAuth();

  // Signed in — by the password form below, or already — means this page is
  // done. Without this the form just sat there after a successful sign-in,
  // which read as "my password doesn't work".
  if (session) return <Navigate to="/trips" replace />;

  /**
   * Supabase's raw auth errors are written for developers. Map the ones a user
   * can act on; pass anything unrecognised through rather than swallowing it
   * behind a generic message. Shape borrowed from scholar's friendlySignInError.
   */
  function friendlyError(msg: string): string {
    const m = msg.toLowerCase();
    if (m.includes('invalid login') || m.includes('invalid credentials'))
      // Supabase returns this for a wrong password AND for an account with no
      // password, deliberately — it will not confirm whether an address exists.
      return 'That email and password don’t match. If you’ve only ever signed in with an email link, you don’t have a password yet — use “Set or reset password” below.';
    if (m.includes('email not confirmed') || m.includes('not confirmed'))
      return 'This account isn’t activated yet. Check your inbox for the sign-in link and open it once.';
    if (m.includes('rate limit') || m.includes('too many') || m.includes('over_email'))
      return 'Too many attempts. Wait a few minutes and try again.';
    if (m.includes('network') || m.includes('fetch'))
      return 'Couldn’t reach the server. Check your connection and try again.';
    return msg;
  }

  function switchTo(next: Mode) {
    setMode(next);
    setError(null);
    setSent(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setError(null);

    try {
      if (mode === 'password') {
        const { error: err } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        if (err) setError(friendlyError(err.message));
      } else if (mode === 'link') {
        const { error: err } = await supabase.auth.signInWithOtp({
          email: email.trim(),
          options: { emailRedirectTo: window.location.origin + '/trips' },
        });
        if (err) setError(friendlyError(err.message));
        else setSent('link');
      } else {
        const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
          redirectTo: window.location.origin + '/reset-password',
        });
        if (err) setError(friendlyError(err.message));
        else setSent('reset');
      }
    } finally {
      setBusy(false);
    }
  }

  const copy: Record<Mode, { heading: string; blurb: string; action: string }> = {
    password: {
      heading: 'Welcome back',
      blurb: 'Sign in to plan and curate your trips.',
      action: 'Sign in',
    },
    link: {
      heading: 'Sign in by email',
      blurb: 'We’ll email you a link that signs you straight in.',
      action: 'Email me a link',
    },
    reset: {
      heading: 'Set a password',
      blurb:
        'We’ll email you a link to choose one. Use this the first time too — an account made with an email link doesn’t have a password yet.',
      action: 'Send the link',
    },
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex' }}>
      {/* ── Left: brand panel ── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          width: '54%',
          bgcolor: 'primary.main',
          p: 6,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Contour rings — a map, not a gradient */}
        {[
          { size: 620, right: -200, top: -140, alpha: 0.1 },
          { size: 480, right: -140, top: -80, alpha: 0.08 },
          { size: 340, right: -80, top: -20, alpha: 0.06 },
        ].map((ring) => (
          <Box
            key={ring.size}
            aria-hidden
            sx={{
              position: 'absolute',
              right: ring.right,
              top: ring.top,
              width: ring.size,
              height: ring.size,
              borderRadius: '50%',
              border: `1px solid rgba(232,244,236,${ring.alpha})`,
              pointerEvents: 'none',
            }}
          />
        ))}

        <Typography
          sx={{ fontWeight: 700, fontSize: '1.2rem', color: '#FFFFFF', letterSpacing: '-0.01em' }}
        >
          Roamoir
        </Typography>

        <Box sx={{ position: 'relative' }}>
          <Typography
            variant="h3"
            sx={{ color: '#FFFFFF', fontWeight: 700, lineHeight: 1.15, mb: 1.5, maxWidth: 420 }}
          >
            Where you went.
            <br />
            What it meant.
          </Typography>
          <Typography
            sx={{
              ...serifVoice,
              color: 'rgba(232,244,236,0.72)',
              fontSize: '1rem',
              lineHeight: 1.75,
              maxWidth: 380,
              mb: 5,
            }}
          >
            A travel journal that plans the trip with you, keeps up when there’s
            no signal, and turns the whole thing into something worth sending.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, maxWidth: 520 }}>
            {PILLARS.map((p) => (
              <Box key={p.label}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, color: 'secondary.light' }}
                >
                  {p.icon}
                  <Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.82rem' }}>
                    {p.label}
                  </Typography>
                </Box>
                <Typography sx={{ color: 'rgba(232,244,236,0.55)', fontSize: '0.78rem', lineHeight: 1.6 }}>
                  {p.line}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Typography sx={{ color: 'rgba(232,244,236,0.32)', fontSize: '0.72rem' }}>
          © {new Date().getFullYear()} Roamoir
        </Typography>
      </Box>

      {/* ── Right: form ── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, sm: 6 },
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 4, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', color: 'primary.main' }}>
            Roamoir
          </Typography>
        </Box>

        <Box sx={{ width: '100%', maxWidth: 380 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.75 }}>
            {copy[mode].heading}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.65 }}>
            {copy[mode].blurb}
          </Typography>

          {sent === 'link' ? (
            <Alert severity="success">Check your email for a sign-in link.</Alert>
          ) : sent === 'reset' ? (
            <Alert severity="success">Check your email for a link to set your password.</Alert>
          ) : (
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <TextField
                type="email"
                label="Email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="username"
              />

              {mode === 'password' ? (
                <TextField
                  type="password"
                  label="Password"
                  fullWidth
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              ) : null}

              {error ? <Alert severity="error">{error}</Alert> : null}

              <Button type="submit" variant="contained" fullWidth disabled={busy} sx={{ mt: 1, py: 1.25 }}>
                {busy ? <CircularProgress size={22} color="inherit" /> : copy[mode].action}
              </Button>
            </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
            {mode !== 'password' ? (
              <Link component="button" type="button" variant="body2" onClick={() => switchTo('password')}>
                Sign in with a password
              </Link>
            ) : null}
            {mode !== 'link' ? (
              <Link component="button" type="button" variant="body2" onClick={() => switchTo('link')}>
                Email me a sign-in link instead
              </Link>
            ) : null}
            {mode !== 'reset' ? (
              <Link component="button" type="button" variant="body2" onClick={() => switchTo('reset')}>
                Set or reset password
              </Link>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
